import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Select from 'react-select';
import { initiativesApi } from '../services/trackerApi';
import { peopleApi } from '../services/api';
import { useToast } from '../hooks/useToast';
import { CATEGORIES, milestoneProgress } from '../utils/format';
import { DateField } from '../components/DateField';
import { RichTextField, isRichTextEmpty } from '../components/RichTextField';
import { DocumentManager } from '../components/DocumentManager';
import { looksLikeHtml, plainTextToHtml } from '../components/RichTextView';
import { PageHeader } from '../components/PageHeader';
import { reactSelectStyles } from '../utils/reactSelectStyles';
import type { Initiative, InitiativeTier } from '../types/trackerModels';
import { INITIATIVE_TIERS, PARENT_TIER_FOR } from '../types/trackerModels';

let uid = 0;
const nextId = () => `tmp-${++uid}-${Date.now()}`;

export const InitiativeFormPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEditing = !!id;
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: people = [] } = useQuery({ queryKey: ['people'], queryFn: peopleApi.getAll });
  const { data: allInitiatives = [] } = useQuery<Initiative[]>({ queryKey: ['initiatives'], queryFn: initiativesApi.getAll });
  // Excludes every descendant of the record being edited from its own parent picker, not just
  // itself - selecting a former child as the new parent would otherwise create a real, persisted
  // cycle (confirmed live: changing an ancestor's tier, then picking one of its own children as
  // its new parent once the tier change makes it a valid tier-match, silently produced a 2-node
  // cycle with nothing - client or server - rejecting it).
  const descendantIds = useMemo(() => {
    if (!id) return new Set<string>();
    const childrenOf = new Map<string, string[]>();
    allInitiatives.forEach((i) => {
      if (!i.parentInitiativeId) return;
      const arr = childrenOf.get(i.parentInitiativeId) || [];
      arr.push(i.initiativeId);
      childrenOf.set(i.parentInitiativeId, arr);
    });
    const result = new Set<string>();
    const stack = [...(childrenOf.get(id) || [])];
    while (stack.length) {
      const next = stack.pop()!;
      if (result.has(next)) continue; // already-bad data (a cycle) - don't loop forever over it
      result.add(next);
      stack.push(...(childrenOf.get(next) || []));
    }
    return result;
  }, [allInitiatives, id]);
  const { data: existing } = useQuery<Initiative>({
    queryKey: ['initiative', id],
    queryFn: () => initiativesApi.getOne(id!),
    enabled: isEditing,
  });

  const [name, setName] = useState('');
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [currentSituation, setCurrentSituation] = useState('');
  const [desiredSituation, setDesiredSituation] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [ownerId, setOwnerId] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [status, setStatus] = useState('Not Started');
  const [startDate, setStartDate] = useState('');
  const [originalTargetEndDate, setOriginalTargetEndDate] = useState('');
  // Ongoing/standing work with no real end date (e.g. a recurring stand-up) - hides the
  // Timeline card entirely below, since target dates don't mean anything for something that
  // never finishes.
  const [isBau, setIsBau] = useState(false);
  const [tier, setTier] = useState<InitiativeTier>('');
  const [parentInitiativeId, setParentInitiativeId] = useState('');
  const [functionalRequirements, setFR] = useState<{ id: string; text: string; status: string }[]>([]);
  const [nonFunctionalRequirements, setNFR] = useState<{ id: string; text: string; status: string }[]>([]);
  const [inScope, setInScope] = useState<{ id: string; text: string; status: string }[]>([]);
  const [outOfScope, setOutOfScope] = useState<{ id: string; text: string; status: string }[]>([]);
  const [milestones, setMilestones] = useState<{ id: string; name: string; targetDate: string; status: string; weight?: number; completionPercent?: number }[]>([]);
  const [stakeholders, setStakeholders] = useState<{ name: string; role: string }[]>([]);
  const [loaded, setLoaded] = useState(!isEditing);

  useEffect(() => {
    if (existing && !loaded) {
      setName(existing.name);
      setSummary(existing.summary);
      // Descriptions predating the rich-text editor are plain text with newlines. Quill parses
      // whatever it is handed as HTML, so those newlines would collapse and the paragraphs
      // would be flattened on the next save — convert on the way in instead.
      setDescription(
        looksLikeHtml(existing.description ?? '')
          ? existing.description
          : plainTextToHtml(existing.description ?? ''),
      );
      // Unlike description, these two never predate the rich-text editor - no plain-text
      // legacy data to convert.
      setCurrentSituation(existing.currentSituation || '');
      setDesiredSituation(existing.desiredSituation || '');
      setCategory(existing.category || CATEGORIES[0]);
      setOwnerId(existing.ownerId);
      setPriority(existing.priority);
      setStatus(existing.status);
      setStartDate(existing.startDate);
      setOriginalTargetEndDate(existing.originalTargetEndDate);
      setIsBau(!!existing.isBau);
      setTier(existing.tier || '');
      setParentInitiativeId(existing.parentInitiativeId || '');
      setFR(existing.functionalRequirements || []);
      setNFR(existing.nonFunctionalRequirements || []);
      setInScope(existing.inScope || []);
      setOutOfScope(existing.outOfScope || []);
      setMilestones(existing.milestones || []);
      setStakeholders(existing.stakeholders || []);
      setLoaded(true);
    }
  }, [existing, loaded]);

  const payload = () => ({
    name, summary,
    // An editor the user typed in and then cleared still serialises to "<p><br></p>" — store
    // an empty string so "no description" stays a single state everything else can test.
    description: isRichTextEmpty(description) ? '' : description,
    currentSituation: isRichTextEmpty(currentSituation) ? '' : currentSituation,
    desiredSituation: isRichTextEmpty(desiredSituation) ? '' : desiredSituation,
    category, ownerId, priority, isBau,
    tier,
    parentInitiativeId: tier === 'Portfolio' ? '' : parentInitiativeId,
    // Standalone Work initiatives never had a start/target date to begin with, and neither
    // does a Portfolio/Programme - clearing them on save rather than just hiding the fields
    // means one flipped to either later doesn't keep a stale timeline hanging around
    // invisibly (still showing up in the Overview/Hub Gantt charts, which only check for the
    // dates being present, not tier).
    startDate: (isBau || tier === 'Portfolio' || tier === 'Program') ? '' : startDate,
    originalTargetEndDate: (isBau || tier === 'Portfolio' || tier === 'Program') ? '' : originalTargetEndDate,
    functionalRequirements: functionalRequirements.map(({ id, text, status }) => ({ id, text, status })),
    nonFunctionalRequirements: nonFunctionalRequirements.map(({ id, text, status }) => ({ id, text, status })),
    inScope: inScope.map(({ id, text, status }) => ({ id, text, status })),
    outOfScope: outOfScope.map(({ id, text, status }) => ({ id, text, status })),
    milestones,
    stakeholders,
  });

  const saveMutation = useMutation({
    mutationFn: () => (isEditing ? initiativesApi.update(id!, { ...payload(), status }) : initiativesApi.create(payload())),
    onSuccess: (initiative) => {
      toast.success(isEditing ? 'Initiative updated.' : 'Initiative created.');
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
      queryClient.invalidateQueries({ queryKey: ['initiative', initiative.initiativeId] });
      navigate(`/tracker/initiatives/${initiative.initiativeId}`);
    },
    onError: (e: any) => toast.error(e.message || 'Failed to save initiative'),
  });

  const milestoneWeightTotal = milestones.reduce((sum, m) => sum + (m.weight || 0), 0);

  if (isEditing && !loaded) return <p className="np-sub container-xxl flex-grow-1 container-p-y">Loading…</p>;

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-flashlight-line"
        title={isEditing ? 'Edit Initiative' : 'New Initiative'}
        breadcrumbs={[{ label: 'Initiative Tracker', to: '/tracker' }, { label: isEditing ? 'Edit Initiative' : 'New Initiative' }]}
      />
      {/* No inner max-width - container-xxl's own cap (Bootstrap default ~1320px) is already
          the right scale-with-viewport behaviour; a fixed 780px here squeezed the Current/
          Desired Situation pair (a 2-column row) down to ~370px per card, uncomfortably
          narrow for a rich-text editor plus attachments. Matches InterestedPartyFormPage.tsx,
          which never added an inner cap for the same reason. */}
      <div>

        <div className="np-card mb-3">
          <div className="np-card-head"><h2>Basics</h2></div>
          <div className="np-card-body">
            <div className="np-field">
              <label htmlFor="init-name">Name</label>
              <input id="init-name" className="np-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Zero Trust Network Access Rollout" />
            </div>
            <div className="np-field">
              <label htmlFor="init-summary">One-line summary</label>
              <input id="init-summary" className="np-input" value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="The one sentence that explains this initiative to someone who has never heard of it" />
            </div>
            {/* No htmlFor: the control is a Quill editor, not a labellable form element. */}
            <div className="np-field">
              <label>Description</label>
              <RichTextField
                value={description}
                onChange={setDescription}
                placeholder="What is this initiative, what is in scope, and what is deliberately not? Use paragraphs and lists — this is the long-form record other people read first."
                height={260}
              />
            </div>

            <div className="row g-3">
              <div className="col-md-3">
                <div className="np-field mb-0">
                  <label htmlFor="init-category">Category</label>
                  <select id="init-category" className="np-select" value={category} onChange={(e) => setCategory(e.target.value)}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="col-md-3">
                <div className="np-field mb-0">
                  <label htmlFor="init-owner">Owner</label>
                  <Select
                    inputId="init-owner"
                    options={people.map((p: any) => ({ value: p.personId, label: p.personName }))}
                    value={(() => {
                      const opt = people.find((p) => p.personId === ownerId);
                      return opt ? { value: opt.personId, label: opt.personName } : null;
                    })()}
                    onChange={(opt) => setOwnerId(opt?.value ?? '')}
                    placeholder="— select —"
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={reactSelectStyles()}
                  />
                </div>
              </div>
              <div className="col-md-3">
                <div className="np-field mb-0">
                  <label htmlFor="init-priority">Priority</label>
                  <select id="init-priority" className="np-select" value={priority} onChange={(e) => setPriority(e.target.value)}>
                    {['Low', 'Medium', 'High', 'Critical'].map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              {isEditing && (
                <div className="col-md-3">
                  <div className="np-field mb-0">
                    <label htmlFor="init-status">Status</label>
                    <select id="init-status" className="np-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                      {['Not Started', 'In Progress', 'Blocked', 'On Hold', 'Completed', 'Cancelled'].map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div className="row g-3 mt-1">
              <div className="col-md-4">
                <div className="np-field mb-0">
                  <label htmlFor="init-tier">Tier</label>
                  <select
                    id="init-tier"
                    className="np-select"
                    value={tier}
                    onChange={(e) => {
                      setTier(e.target.value as InitiativeTier);
                      // The valid parent tier changes (or disappears, for Portfolio) whenever
                      // tier changes - clearing rather than silently keeping a now-invalid
                      // selection around.
                      setParentInitiativeId('');
                    }}
                  >
                    <option value="">— none —</option>
                    {INITIATIVE_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <p className="np-hint mb-0">Portfolio management tier — optional, but required to link this into a hierarchy.</p>
                </div>
              </div>
              {(tier === 'Program' || tier === 'Project') && (
                <div className="col-md-4">
                  <div className="np-field mb-0">
                    <label htmlFor="init-parent">{PARENT_TIER_FOR[tier]}</label>
                    <select
                      id="init-parent"
                      className="np-select"
                      value={parentInitiativeId}
                      onChange={(e) => setParentInitiativeId(e.target.value)}
                    >
                      <option value="">— none —</option>
                      {allInitiatives
                        .filter((i) => i.tier === PARENT_TIER_FOR[tier] && i.initiativeId !== id && !descendantIds.has(i.initiativeId))
                        .map((i) => <option key={i.initiativeId} value={i.initiativeId}>{i.name}</option>)}
                    </select>
                    <p className="np-hint mb-0">The {PARENT_TIER_FOR[tier].toLowerCase()} this rolls up into.</p>
                  </div>
                </div>
              )}
            </div>

            <div className="row g-3 mt-1">
              <div className="col-12">
                <label className="d-flex align-items-start gap-2" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={isBau}
                    onChange={(e) => setIsBau(e.target.checked)}
                  />
                  <span>
                    <span className="fw-semibold d-block">Standalone Work (ongoing, no end date)</span>
                    <span className="np-sub">
                      For standing work like a recurring stand-up — e.g. to centralise its meeting minutes here.
                      Hides the Timeline below and keeps it out of the portfolio's progress/health tiles and
                      the timeline chart, which assume a project with a start and an end.
                    </span>
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* A Portfolio or Programme doesn't carry its own start/target-end date the way a
            Project does - same reasoning as Standalone Work, which is why this hides for both. */}
        {!isBau && tier !== 'Portfolio' && tier !== 'Program' && (
        <div className="np-card mb-3">
          <div className="np-card-head"><h2>Timeline</h2></div>
          <div className="np-card-body">
            <div className="row g-3">
              <div className="col-md-6">
                <div className="np-field mb-0">
                  <label>Start date</label>
                  <DateField className="np-input" value={startDate} onChange={setStartDate} />
                </div>
              </div>
              <div className="col-md-6">
                <div className="np-field mb-0">
                  <label>
                    Original target end date {isEditing && <span className="np-sub fw-normal">— the baseline, locked once set</span>}
                  </label>
                  <DateField
                    className="np-input"
                    value={originalTargetEndDate}
                    disabled={isEditing && !!existing?.originalTargetEndDate}
                    onChange={setOriginalTargetEndDate}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Research the landscape, decide the target, then Scope below is set against the gap
            between the two - same field-in-a-card pattern as Description above, folded into
            this one whole-form Save. Attachments are the one thing on this pair that ISN'T
            part of that save - DocumentManager writes straight through its own upload/delete
            endpoints - and only render once editing an existing initiative, since there's no
            entityId to attach to until the first Save creates one. Uploading/removing a file
            is an edit action, so it lives here and not on the view page (which shows the same
            attachments read-only, download-only). */}
        <div className="row g-3">
          <div className="col-md-6">
            <div className="np-card mb-3 np-card-accent-neutral">
              <div className="np-card-head">
                <span className="np-icon-badge np-icon-badge-sm np-tone-neutral me-2"><i className="ri-map-2-line"></i></span>
                <h2 className="mb-0">Current Situation</h2>
              </div>
              <div className="np-card-body">
                <RichTextField
                  value={currentSituation}
                  onChange={setCurrentSituation}
                  placeholder="What exists today? Document the current landscape - systems, processes, pain points - with enough detail that Scope below can be set accurately against it."
                  height={180}
                />
                {isEditing && <DocumentManager embedded entityType="initiative_current_situation" entityId={id!} />}
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="np-card mb-3 np-card-accent-primary">
              <div className="np-card-head">
                <span className="np-icon-badge np-icon-badge-sm np-tone-primary me-2"><i className="ri-flag-line"></i></span>
                <h2 className="mb-0">Desired Situation</h2>
              </div>
              <div className="np-card-body">
                <RichTextField
                  value={desiredSituation}
                  onChange={setDesiredSituation}
                  placeholder="What should exist once this initiative is done? Set the target state - this, together with Current Situation, is what Scope below should be set accurately against."
                  height={180}
                />
                {isEditing && <DocumentManager embedded entityType="initiative_desired_situation" entityId={id!} />}
              </div>
            </div>
          </div>
        </div>

        <div className="np-card mb-3">
          <div className="np-card-head"><h2>Scope</h2></div>
          <div className="np-card-body">
            <ListEditor
              label="In Scope"
              items={inScope}
              onAdd={() => setInScope((prev) => [...prev, { id: nextId(), text: '', status: 'Proposed' }])}
              onRemove={(id) => setInScope((prev) => prev.filter((x) => x.id !== id))}
              onUpdate={(next) => setInScope((prev) => prev.map((x) => (x.id === next.id ? next : x)))}
              render={(item, update) => (
                <input className="np-input" placeholder="What this initiative covers…" value={item.text} onChange={(e) => update({ ...item, text: e.target.value })} />
              )}
            />

            <ListEditor
              label="Out of Scope"
              items={outOfScope}
              onAdd={() => setOutOfScope((prev) => [...prev, { id: nextId(), text: '', status: 'Proposed' }])}
              onRemove={(id) => setOutOfScope((prev) => prev.filter((x) => x.id !== id))}
              onUpdate={(next) => setOutOfScope((prev) => prev.map((x) => (x.id === next.id ? next : x)))}
              render={(item, update) => (
                <input className="np-input" placeholder="What it deliberately does not cover…" value={item.text} onChange={(e) => update({ ...item, text: e.target.value })} />
              )}
            />
          </div>
        </div>

        <div className="np-card mb-3">
          <div className="np-card-head"><h2>Requirements</h2></div>
          <div className="np-card-body">
            {/* Functional Requirements */}
            <ListEditor
              label="Functional Requirements"
              items={functionalRequirements}
              onAdd={() => setFR((prev) => [...prev, { id: nextId(), text: '', status: 'Proposed' }])}
              onRemove={(id) => setFR((prev) => prev.filter((x) => x.id !== id))}
              onUpdate={(next) => setFR((prev) => prev.map((x) => (x.id === next.id ? next : x)))}
              render={(item, update) => (
                <input className="np-input" placeholder="Requirement…" value={item.text} onChange={(e) => update({ ...item, text: e.target.value })} />
              )}
            />

            <ListEditor
              label="Non-Functional Requirements"
              items={nonFunctionalRequirements}
              onAdd={() => setNFR((prev) => [...prev, { id: nextId(), text: '', status: 'Proposed' }])}
              onRemove={(id) => setNFR((prev) => prev.filter((x) => x.id !== id))}
              onUpdate={(next) => setNFR((prev) => prev.map((x) => (x.id === next.id ? next : x)))}
              render={(item, update) => (
                <input className="np-input" placeholder="e.g. [Security] All access logged to SIEM" value={item.text} onChange={(e) => update({ ...item, text: e.target.value })} />
              )}
            />
          </div>
        </div>

        <div className="np-card mb-3">
          <div className="np-card-head" style={{ justifyContent: 'space-between' }}>
            <h2>Milestones</h2>
            {milestoneWeightTotal > 0 && (
              <span className={`np-chip np-tone-${milestoneWeightTotal === 100 ? 'success' : 'warning'}`}>
                {milestoneWeightTotal}% of completion weight allocated
              </span>
            )}
          </div>
          <div className="np-card-body">
            <div className="d-flex justify-content-end mb-2">
              <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={() => setMilestones((prev) => [...prev, { id: nextId(), name: '', targetDate: '', status: 'Upcoming', weight: 0 }])}>
                <i className="ri-add-line"></i> Add milestone
              </button>
            </div>
            {milestones.length === 0 && <p className="np-sub mb-0">No milestones yet.</p>}
            {milestones.map((m) => {
              const progress = milestoneProgress(m);
              return (
                <div key={m.id} className="np-row-card">
                  <div className="row g-2 mb-2 align-items-center">
                    <div className="col-5"><input className="np-input" placeholder="Milestone name" value={m.name} onChange={(e) => setMilestones((prev) => prev.map((x) => x.id === m.id ? { ...x, name: e.target.value } : x))} /></div>
                    <div className="col-3"><DateField className="np-input" value={m.targetDate} onChange={(v) => setMilestones((prev) => prev.map((x) => x.id === m.id ? { ...x, targetDate: v } : x))} /></div>
                    <div className="col-3">
                      <select
                        className="np-select"
                        value={m.status}
                        onChange={(e) => setMilestones((prev) => prev.map((x) => x.id === m.id ? {
                          ...x,
                          status: e.target.value,
                          completionPercent: e.target.value === 'Done' && x.completionPercent == null ? 100 : x.completionPercent,
                        } : x))}
                      >
                        {['Upcoming', 'Done', 'At Risk', 'Missed'].map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="col-1"><button type="button" className="np-icon-btn np-icon-btn-danger" onClick={() => setMilestones((prev) => prev.filter((x) => x.id !== m.id))}><i className="ri-close-line"></i></button></div>
                  </div>
                  <div className="row g-2 align-items-center">
                    <div className="col-3">
                      <div className="input-group input-group-sm">
                        <span className="input-group-text" style={{ fontSize: 11 }}>Weight</span>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          className="np-input"
                          value={m.weight || ''}
                          onChange={(e) => setMilestones((prev) => prev.map((x) => x.id === m.id ? { ...x, weight: Number(e.target.value) } : x))}
                        />
                        <span className="input-group-text">%</span>
                      </div>
                    </div>
                    <div className="col-3">
                      <div className="input-group input-group-sm">
                        <span className="input-group-text" style={{ fontSize: 11 }}>Complete</span>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          className="np-input"
                          value={m.completionPercent ?? ''}
                          onChange={(e) => setMilestones((prev) => prev.map((x) => x.id === m.id ? { ...x, completionPercent: e.target.value === '' ? undefined : Number(e.target.value) } : x))}
                        />
                        <span className="input-group-text">%</span>
                      </div>
                    </div>
                    <div className="col-6 d-flex align-items-center">
                      <div className="np-progress" style={{ width: '100%' }}>
                        <div className="np-fill" style={{ width: `${progress}%` }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {milestones.length > 0 && (
              <p className="np-hint mt-1">
                Weight determines how much each milestone contributes to the initiative's overall completion %; Complete is how far along that specific milestone is. Leave weight at 0 to exclude a milestone from the calculation.
              </p>
            )}
          </div>
        </div>

        <div className="np-card mb-3">
          <div className="np-card-head"><h2>Stakeholders</h2></div>
          <div className="np-card-body">
            <div className="d-flex justify-content-end mb-2">
              <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={() => setStakeholders((prev) => [...prev, { name: '', role: 'Informed' }])}>
                <i className="ri-add-line"></i> Add stakeholder
              </button>
            </div>
            {stakeholders.length === 0 && <p className="np-sub mb-0">No stakeholders yet.</p>}
            {stakeholders.map((s, idx) => (
              <div key={idx} className="row g-2 mb-2 align-items-center">
                <div className="col-6"><input className="np-input" placeholder="Name" value={s.name} onChange={(e) => setStakeholders((prev) => prev.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))} /></div>
                <div className="col-4">
                  <select className="np-select" value={s.role} onChange={(e) => setStakeholders((prev) => prev.map((x, i) => i === idx ? { ...x, role: e.target.value } : x))}>
                    {['Sponsor', 'Owner', 'Consulted', 'Informed'].map((r) => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div className="col-2"><button type="button" className="np-icon-btn np-icon-btn-danger" onClick={() => setStakeholders((prev) => prev.filter((_, i) => i !== idx))}><i className="ri-close-line"></i></button></div>
              </div>
            ))}
          </div>
        </div>

        <div className="d-flex gap-2">
          <button className="np-btn np-btn-primary" disabled={!name.trim() || saveMutation.isPending} onClick={() => saveMutation.mutate()}>
            {saveMutation.isPending ? <span className="spinner-border spinner-border-sm"></span> : <i className="ri-save-line"></i>}
            {isEditing ? 'Save changes' : 'Create Initiative'}
          </button>
          {isEditing && (
            <button className="np-btn np-btn-ghost" onClick={() => navigate(`/tracker/initiatives/${id}`)}>Cancel</button>
          )}
        </div>
      </div>
    </div>
  );
};

function ListEditor<T extends { id: string }>({
  label, items, onAdd, onRemove, onUpdate, render,
}: {
  label: string;
  items: T[];
  onAdd: () => void;
  onRemove: (id: string) => void;
  onUpdate: (next: T) => void;
  render: (item: T, update: (next: T) => void) => React.ReactNode;
}) {
  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="fw-semibold mb-0">{label}</label>
        <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={onAdd}>
          <i className="ri-add-line"></i> Add
        </button>
      </div>
      {items.map((item) => (
        <div key={item.id} className="d-flex gap-2 mb-2">
          <div className="flex-grow-1">{render(item, onUpdate)}</div>
          <button type="button" className="np-icon-btn np-icon-btn-danger" onClick={() => onRemove(item.id)}><i className="ri-close-line"></i></button>
        </div>
      ))}
    </div>
  );
}
